/**
 * CaretRenderingEngine - High-performance visual caret rendering
 * 
 * Provides sophisticated caret visualization for legal document editing:
 * - Smooth animations and visual feedback
 * - Context-aware styling (citations, clauses, errors)
 * - High-DPI rendering support
 * - Accessibility indicators
 * - Performance-optimized canvas operations
 */

import { CaretState } from '../managers/SmartCaretManager';
import { CaretPosition, PerformanceMetrics } from '../interfaces/CaretInterfaces';
import { CoordinateSpace } from '../core/UnifiedCoordinateSystem';

export interface CaretRenderingConfig {
  // Visual appearance
  width: number;                    // Caret width in pixels
  color: string;                    // Default caret color
  opacity: number;                  // Caret opacity (0-1)
  
  // Context-aware styling
  citationColor: string;            // Color when in citations
  clauseColor: string;              // Color when in clauses  
  errorColor: string;               // Color when indicating errors
  selectionColor: string;           // Color during selection
  
  // Animation settings
  animationEasing: 'linear' | 'ease' | 'ease-in' | 'ease-out' | 'ease-in-out';
  fadeInDuration: number;           // Fade in animation duration (ms)
  fadeOutDuration: number;          // Fade out animation duration (ms)
  
  // Performance settings
  enableSubPixelRendering: boolean; // Sub-pixel positioning
  enableHighDPI: boolean;           // High-DPI rendering
  maxRenderFPS: number;             // Maximum render frame rate
  
  // Accessibility
  enableAccessibilityIndicator: boolean;
  accessibilityIndicatorSize: number;
  
  // Debug visualization
  debugMode: boolean;
  showHitTestBounds: boolean;
  showPositionInfo: boolean;
}

export interface RenderingMetrics {
  framesRendered: number;
  averageFrameTime: number;
  lastRenderTime: number;
  skippedFrames: number;
  performanceScore: number;         // 0-100, higher is better
}

export class CaretRenderingEngine {
  private canvas: HTMLCanvasElement;
  private context: CanvasRenderingContext2D;
  private config: CaretRenderingConfig;
  private metrics: RenderingMetrics;
  
  // Animation state
  private animationFrameId: number | null = null;
  private lastFrameTime: number = 0;
  private frameTimeBuffer: number[] = [];
  
  // Rendering optimization
  private devicePixelRatio: number;
  private renderingScale: number;
  private lastRenderedState: CaretState | null = null;
  private isDirty: boolean = false;
  
  // Performance monitoring
  private performanceStartTime: number = 0;
  private frameDropThreshold: number = 16.67; // 60 FPS threshold
  
  constructor(canvas: HTMLCanvasElement, config: Partial<CaretRenderingConfig> = {}) {
    this.canvas = canvas;
    const context = canvas.getContext('2d');
    
    if (!context) {
      throw new Error('Failed to get 2D rendering context');
    }
    
    this.context = context;
    
    // Initialize configuration with defaults
    this.config = {
      // Visual defaults
      width: 2,
      color: '#000000',
      opacity: 1.0,
      
      // Context-aware colors
      citationColor: '#0066CC',      // Blue for citations
      clauseColor: '#CC6600',        // Orange for clauses
      errorColor: '#CC0000',         // Red for errors
      selectionColor: '#0099FF',     // Light blue for selection
      
      // Animation defaults
      animationEasing: 'ease-out',
      fadeInDuration: 150,
      fadeOutDuration: 100,
      
      // Performance defaults
      enableSubPixelRendering: true,
      enableHighDPI: true,
      maxRenderFPS: 60,
      
      // Accessibility defaults
      enableAccessibilityIndicator: true,
      accessibilityIndicatorSize: 4,
      
      // Debug defaults
      debugMode: false,
      showHitTestBounds: false,
      showPositionInfo: false,
      
      ...config
    };
    
    // Initialize metrics
    this.metrics = {
      framesRendered: 0,
      averageFrameTime: 0,
      lastRenderTime: 0,
      skippedFrames: 0,
      performanceScore: 100
    };
    
    // Setup high-DPI rendering
    this.devicePixelRatio = window.devicePixelRatio || 1;
    this.renderingScale = this.config.enableHighDPI ? this.devicePixelRatio : 1;
    
    this.setupCanvas();

    if (this.config.debugMode) {
      console.log('[CaretRenderingEngine] Initialized with high-performance rendering');
    }
  }
  
  // ============================================================================
  // CANVAS SETUP AND OPTIMIZATION
  // ============================================================================
  
  /**
   * Setup canvas for optimal rendering
   */
  private setupCanvas(): void {
    // Set up high-DPI rendering
    if (this.config.enableHighDPI) {
      const rect = this.canvas.getBoundingClientRect();
      
      // Scale canvas for high-DPI displays
      this.canvas.width = rect.width * this.devicePixelRatio;
      this.canvas.height = rect.height * this.devicePixelRatio;
      
      // Scale context to match device pixel ratio
      this.context.scale(this.devicePixelRatio, this.devicePixelRatio);

      if (this.config.debugMode) {
        console.log(`[CaretRenderingEngine] High-DPI setup: ${this.devicePixelRatio}x scaling`);
      }
    }
    
    // Optimize context settings
    this.context.imageSmoothingEnabled = true;
    this.context.imageSmoothingQuality = 'high';
    
    // Set text rendering optimization (if supported)
    if ('textRenderingOptimization' in this.context) {
      (this.context as any).textRenderingOptimization = 'optimizeSpeed';
    }
  }
  
  /**
   * Update canvas size (call when container resizes)
   */
  updateCanvasSize(width: number, height: number): void {
    const scaledWidth = width * this.renderingScale;
    const scaledHeight = height * this.renderingScale;
    
    if (this.canvas.width !== scaledWidth || this.canvas.height !== scaledHeight) {
      this.canvas.width = scaledWidth;
      this.canvas.height = scaledHeight;

      if (this.config.enableHighDPI) {
        this.context.scale(this.devicePixelRatio, this.devicePixelRatio);
      }

      this.isDirty = true;
      if (this.config.debugMode) {
        console.log(`[CaretRenderingEngine] Canvas resized: ${width}x${height}`);
      }
    }
  }
  
  // ============================================================================
  // MAIN RENDERING API
  // ============================================================================
  
  /**
   * Render caret with current state
   */
  render(state: CaretState): void {
    
    // Performance gate: skip rendering if not needed
    if (!this.shouldRender(state)) {
      if (this.config.debugMode) {
        console.log('[CaretRenderingEngine] Skipping render - shouldRender returned false');
      }
      return;
    }
    
    const startTime = performance.now();
    
    // Clear previous caret (only the caret area for performance)
    this.clearCaretArea(this.lastRenderedState);
    
    // Render new caret if visible
    if (state.isVisible && state.position) {
      this.renderCaret(state);
      
      // Render debug information if enabled
      if (this.config.debugMode) {
        this.renderDebugInfo(state);
      }
      
      // Render accessibility indicator if enabled (DISABLED for MS Word/Google Docs style)
      // if (this.config.enableAccessibilityIndicator) {
      //   this.renderAccessibilityIndicator(state);
      // }
    } else {
      console.log('[CaretRenderingEngine] Not rendering caret:', {
        reason: !state.isVisible ? 'not visible' : 'no position'
      });
    }
    
    // Update performance metrics
    this.updateRenderingMetrics(startTime);
    
    // Store state for next frame comparison
    this.lastRenderedState = { ...state };
    this.isDirty = false;
  }

  /**
   * Render caret with external context (from PaperCanvas)
   */
  renderWithContext(state: CaretState, externalContext: CanvasRenderingContext2D): void {
    
    // Store original context
    const originalContext = this.context;
    
    try {
      // Use external context
      this.context = externalContext;
      
      // Always render when using external context (no performance optimization)
      if (state.isVisible && state.position) {
        this.renderCaret(state);
        
        // Render debug information if enabled
        if (this.config.debugMode) {
          this.renderDebugInfo(state);
        }
        
        // Render accessibility indicator if enabled (DISABLED for MS Word/Google Docs style)
        // if (this.config.enableAccessibilityIndicator) {
        //   this.renderAccessibilityIndicator(state);
        // }
      } else {
        if (this.config.debugMode) {
          console.log('[CaretRenderingEngine] Not rendering caret with external context:', {
            reason: !state.isVisible ? 'not visible' : 'no position'
          });
        }
      }
      
    } finally {
      // Restore original context
      this.context = originalContext;
    }
  }
  
  /**
   * Start continuous rendering loop (for animations)
   */
  startRenderLoop(getState: () => CaretState): void {
    if (this.animationFrameId !== null) {
      return; // Already running
    }
    
    const renderFrame = (timestamp: number) => {
      // Throttle rendering to max FPS
      if (timestamp - this.lastFrameTime >= 1000 / this.config.maxRenderFPS) {
        const currentState = getState();
        this.render(currentState);
        this.lastFrameTime = timestamp;
      }
      
      this.animationFrameId = requestAnimationFrame(renderFrame);
    };
    
    this.animationFrameId = requestAnimationFrame(renderFrame);
    if (this.config.debugMode) {
      console.log('[CaretRenderingEngine] Render loop started');
    }
  }

  /**
   * Stop continuous rendering loop
   */
  stopRenderLoop(): void {
    if (this.animationFrameId !== null) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
      if (this.config.debugMode) {
        console.log('[CaretRenderingEngine] Render loop stopped');
      }
    }
  }
  
  // ============================================================================
  // CARET RENDERING METHODS
  // ============================================================================
  
  /**
   * Render the main caret visual - MS Word/Google Docs style
   */
  private renderCaret(state: CaretState): void {
    if (!state.position) return;
    
    const position = state.position;
    const coords = position.coordinates;
    
    // Set rendering properties - simple black vertical line
    this.context.fillStyle = '#000000';
    this.context.globalAlpha = state.isVisible ? 1.0 : 0.0;
    
    // Calculate caret dimensions - simple vertical line
    const caretX = Math.floor(coords.x);
    const caretY = Math.floor(coords.y);
    const caretWidth = 1; // Thin line like MS Word/Google Docs
    const textHeight = this.calculateCaretHeight(state);
    
    // MS Word exact behavior: caret height matches font size exactly
    const caretHeight = textHeight;
    
    // MS Word positioning: caret sits on the baseline (bottom of text)
    const baselineY = caretY + textHeight;
    const caretTopY = baselineY - caretHeight;

    // Render simple vertical line - MS Word exact style (sits on baseline, height matches font size)
    this.context.fillRect(caretX, caretTopY, caretWidth, caretHeight);
    
    // Reset global alpha
    this.context.globalAlpha = 1.0;
  }
  
  /**
   * Get simple caret styling - MS Word/Google Docs style
   */
  private getCaretStyle(state: CaretState): {
    color: string;
    opacity: number;
    width: number;
    showCap: boolean;
  } {
    // Simple styling - just like MS Word/Google Docs
    return {
      color: '#000000',
      opacity: state.isVisible ? 1.0 : 0.0,
      width: 1,
      showCap: false // No cap, just a simple line
    };
  }
  
  /**
   * Calculate appropriate caret height - MS Word/Google Docs style
   */
  private calculateCaretHeight(state: CaretState): number {
    if (!state.position) return 20; // Default height
    
    const context = state.context;
    
    // Simple height calculation - match font size like MS Word/Google Docs
    let height = context.fontSize || 20;

    if (this.config.debugMode) {
      console.log('[CaretRenderingEngine] Font size scaling:', {
        contextFontSize: context.fontSize,
        calculatedHeight: height,
        roundedHeight: Math.round(height),
        fontFamily: context.fontFamily
      });
    }
    
    return Math.round(height);
  }
  
  // ============================================================================
  // ANIMATION EFFECTS
  // ============================================================================
  
  /**
   * Apply fade-in animation effect
   */
  private applyFadeInEffect(x: number, y: number, width: number, height: number): void {
    // Create gradient for fade effect
    const gradient = this.context.createLinearGradient(x, y - height, x, y);
    gradient.addColorStop(0, 'rgba(0, 0, 0, 0)');
    gradient.addColorStop(1, this.config.color);
    
    this.context.fillStyle = gradient;
    this.context.fillRect(x, y - height, width, height);
  }
  
  /**
   * Apply fade-out animation effect
   */
  private applyFadeOutEffect(x: number, y: number, width: number, height: number): void {
    // Create gradient for fade effect
    const gradient = this.context.createLinearGradient(x, y - height, x, y);
    gradient.addColorStop(0, this.config.color);
    gradient.addColorStop(1, 'rgba(0, 0, 0, 0)');
    
    this.context.fillStyle = gradient;
    this.context.fillRect(x, y - height, width, height);
  }
  
  // ============================================================================
  // DEBUG AND ACCESSIBILITY RENDERING
  // ============================================================================
  
  /**
   * Render debug information
   */
  private renderDebugInfo(state: CaretState): void {
    if (!state.position) return;
    
    const position = state.position;
    const coords = position.coordinates;
    
    // Save context state
    this.context.save();
    
    // Render position info
    if (this.config.showPositionInfo) {
      this.context.fillStyle = '#FF0000';
      this.context.font = '10px monospace';
      this.context.fillText(
        `Index: ${position.index}, X: ${coords.x.toFixed(1)}, Y: ${coords.y.toFixed(1)}`,
        coords.x + 10,
        coords.y - 20
      );
    }
    
    // Render hit test bounds
    if (this.config.showHitTestBounds && position.metadata?.debugInfo?.characterBox) {
      const [boxX, boxY, boxW, boxH] = position.metadata.debugInfo.characterBox;
      
      this.context.strokeStyle = '#00FF00';
      this.context.lineWidth = 1;
      this.context.strokeRect(boxX, boxY, boxW, boxH);
    }
    
    // Restore context state
    this.context.restore();
  }
  
  /**
   * Render accessibility indicator
   */
  private renderAccessibilityIndicator(state: CaretState): void {
    if (!state.position) return;
    
    const coords = state.position.coordinates;
    const size = this.config.accessibilityIndicatorSize;
    
    // Render small indicator dot
    this.context.save();
    this.context.fillStyle = '#FFFFFF';
    this.context.strokeStyle = '#000000';
    this.context.lineWidth = 1;
    
    this.context.beginPath();
    this.context.arc(coords.x - size - 2, coords.y - size - 2, size, 0, 2 * Math.PI);
    this.context.fill();
    this.context.stroke();
    
    this.context.restore();
  }
  
  // ============================================================================
  // PERFORMANCE OPTIMIZATION
  // ============================================================================
  
  /**
   * Determine if rendering is needed
   */
  private shouldRender(state: CaretState): boolean {
    // Always render if dirty or no previous state
    if (this.isDirty || !this.lastRenderedState) {
      if (this.config.debugMode) {
        console.log('[CaretRenderingEngine] shouldRender: true (dirty or no previous state)');
      }
      return true;
    }
    
    const prev = this.lastRenderedState;
    
    // Check for state changes that require re-rendering
    if (state.isVisible !== prev.isVisible) {
      if (this.config.debugMode) {
        console.log('[CaretRenderingEngine] shouldRender: true (visibility changed)');
      }
      return true;
    }
    if (state.animationState !== prev.animationState) {
      if (this.config.debugMode) {
        console.log('[CaretRenderingEngine] shouldRender: true (animation state changed)');
      }
      return true;
    }
    
    // Check position changes
    if (state.position && prev.position) {
      const currentCoords = state.position.coordinates;
      const prevCoords = prev.position.coordinates;
      
      if (Math.abs(currentCoords.x - prevCoords.x) > 0.1) {
        if (this.config.debugMode) {
          console.log('[CaretRenderingEngine] shouldRender: true (x position changed)');
        }
        return true;
      }
      if (Math.abs(currentCoords.y - prevCoords.y) > 0.1) {
        if (this.config.debugMode) {
          console.log('[CaretRenderingEngine] shouldRender: true (y position changed)');
        }
        return true;
      }
    } else if (state.position !== prev.position) {
      if (this.config.debugMode) {
        console.log('[CaretRenderingEngine] shouldRender: true (position object changed)');
      }
      return true;
    }

    if (this.config.debugMode) {
      console.log('[CaretRenderingEngine] shouldRender: false (no changes detected)');
    }
    return false;
  }
  
  /**
   * Clear only the caret area for performance
   */
  private clearCaretArea(prevState: CaretState | null): void {
    if (!prevState || !prevState.position) return;
    
    const coords = prevState.position.coordinates;
    const clearWidth = this.config.width + 10; // Extra margin for effects
    const clearHeight = this.calculateCaretHeight(prevState) + 10;
    
    this.context.clearRect(
      coords.x - 5,
      coords.y - clearHeight,
      clearWidth,
      clearHeight
    );
  }
  
  /**
   * Update rendering performance metrics
   */
  private updateRenderingMetrics(startTime: number): void {
    const renderTime = performance.now() - startTime;
    
    this.metrics.framesRendered++;
    this.metrics.lastRenderTime = renderTime;
    
    // Update frame time buffer for average calculation
    this.frameTimeBuffer.push(renderTime);
    if (this.frameTimeBuffer.length > 60) { // Keep last 60 frames
      this.frameTimeBuffer.shift();
    }
    
    // Calculate average frame time
    this.metrics.averageFrameTime = 
      this.frameTimeBuffer.reduce((sum, time) => sum + time, 0) / this.frameTimeBuffer.length;
    
    // Track dropped frames
    if (renderTime > this.frameDropThreshold) {
      this.metrics.skippedFrames++;
    }
    
    // Calculate performance score
    this.metrics.performanceScore = Math.max(
      0,
      100 - (this.metrics.averageFrameTime / this.frameDropThreshold) * 50
    );
    
    // Log performance warnings
    if (this.metrics.performanceScore < 70 && this.metrics.framesRendered % 100 === 0) {
      console.warn(`[CaretRenderingEngine] Performance warning: Score ${this.metrics.performanceScore.toFixed(1)}, Avg frame time: ${this.metrics.averageFrameTime.toFixed(2)}ms`);
    }
  }
  
  // ============================================================================
  // PUBLIC API
  // ============================================================================
  
  /**
   * Update rendering configuration
   */
  updateConfig(newConfig: Partial<CaretRenderingConfig>): void {
    this.config = { ...this.config, ...newConfig };
    this.isDirty = true;
    
    // Handle special configuration changes
    if (newConfig.enableHighDPI !== undefined) {
      this.renderingScale = newConfig.enableHighDPI ? this.devicePixelRatio : 1;
      this.setupCanvas();
    }
    
    if (this.config.debugMode) {
      console.log('[CaretRenderingEngine] Configuration updated');
    }
  }

  /**
   * Get current rendering metrics
   */
  getMetrics(): RenderingMetrics {
    return { ...this.metrics };
  }

  /**
   * Get performance report
   */
  getPerformanceReport(): string {
    const metrics = this.metrics;

    return `CaretRenderingEngine Performance Report:
  Frames Rendered: ${metrics.framesRendered}
  Average Frame Time: ${metrics.averageFrameTime.toFixed(2)}ms
  Performance Score: ${metrics.performanceScore.toFixed(1)}/100
  Skipped Frames: ${metrics.skippedFrames}
  Last Render Time: ${metrics.lastRenderTime.toFixed(2)}ms`;
  }

  /**
   * Force re-render on next frame
   */
  markDirty(): void {
    this.isDirty = true;
  }

  /**
   * Reset performance metrics
   */
  resetMetrics(): void {
    this.metrics = {
      framesRendered: 0,
      averageFrameTime: 0,
      lastRenderTime: 0,
      skippedFrames: 0,
      performanceScore: 100
    };
    this.frameTimeBuffer = [];

    if (this.config.debugMode) {
      console.log('[CaretRenderingEngine] Metrics reset');
    }
  }
  
  /**
   * Cleanup resources
   */
  destroy(): void {
    this.stopRenderLoop();
    
    // Clear canvas
    this.context.clearRect(0, 0, this.canvas.width, this.canvas.height);

    if (this.config.debugMode) {
      console.log('[CaretRenderingEngine] Destroyed');
    }
  }
}
